import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, FileText, Brain, Users, 
  Sparkles, CheckCircle, ChevronDown, ChevronLeft, ChevronRight,
  ClipboardCheck, BarChart3, Calendar, Play,
  Smartphone, Share, Plus, MoreVertical, ArrowRight,
  FileEdit, Activity, Clock, Zap, Shield, Lightbulb,
  BookOpen, ExternalLink, HelpCircle, Lock, UserCheck, Mail, Instagram
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import LiteProCheckoutModal from '@/components/lite/LiteProCheckoutModal';
import useLitePageView from '@/hooks/useLitePageView';
import DemoFlow from './DemoFlow';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Feature screenshots - mockup visuals for each feature
const FEATURE_SCREENSHOTS = [
  {
    id: 'reports',
    title: 'AI Report Generation',
    description: 'Generate professional clinical reports in minutes with intelligent interpretation of assessment data and session notes.',
    icon: <FileText className="w-5 h-5" />,
    mockupTitle: 'Clinical Report',
    mockupItems: ['Assessment Summary', 'Recommendations', 'Goals Progress', 'Parent-Friendly Version']
  },
  {
    id: 'soap',
    title: 'SOAP Notes & Daysheet',
    description: 'Streamlined session documentation that auto-populates your schedule when notes are saved.',
    icon: <ClipboardCheck className="w-5 h-5" />,
    mockupTitle: 'Session Note',
    mockupItems: ['Subjective', 'Objective', 'Assessment', 'Plan']
  },
  {
    id: 'activities',
    title: 'AI Activity Generator',
    description: 'Create personalized therapy activities based on goals, interests, and communication style.',
    icon: <Sparkles className="w-5 h-5" />,
    mockupTitle: 'Generated Activities',
    mockupItems: ['Play-Based Activities', 'Home Practice Ideas', 'Interest-Based Themes', 'Goal-Aligned Tasks']
  },
  {
    id: 'scheduling',
    title: 'Scheduling & Appointments',
    description: 'Drag-and-drop scheduling with automatic SOAP note integration and patient tracking.',
    icon: <Calendar className="w-5 h-5" />,
    mockupTitle: 'Weekly Daysheet',
    mockupItems: ['Mon-Sun View', 'Time Slots', 'Status Tracking', 'Export & Share']
  },
  {
    id: 'profiles',
    title: 'Child Profiles & Progress',
    description: 'Comprehensive client management with goal tracking, assessments, and parent sharing.',
    icon: <Users className="w-5 h-5" />,
    mockupTitle: 'Client Profile',
    mockupItems: ['Communication Style', 'Interests & Goals', 'Session History', 'Progress Tracking']
  }
];

// Resource Hub articles for SEO
const RESOURCE_ARTICLES = [
  {
    id: 'what-is-glp',
    title: 'What is Gestalt Language Processing?',
    summary: 'A beginner-friendly guide to understanding gestalt language processors, the Natural Language Acquisition (NLA) stages, and how GLP differs from analytical language development.',
    category: 'GLP Basics',
    readTime: '5 min read',
    slug: '/resources/what-is-gestalt-language-processing'
  },
  {
    id: 'affirming-documentation',
    title: 'Writing Neurodiversity-Affirming Reports',
    summary: 'Practical tips for shifting from deficit-based to strengths-based clinical documentation. Includes before/after examples and language swaps you can use today.',
    category: 'Clinical Writing',
    readTime: '7 min read',
    slug: '/resources/neurodiversity-affirming-reports'
  },
  {
    id: 'play-based-strategies',
    title: 'Play-Based Strategies for GLP Kids',
    summary: 'How to use a child\'s special interests and natural play to support language development at each NLA stage. Featuring real-world activity ideas.',
    category: 'Intervention',
    readTime: '6 min read',
    slug: '/resources/play-based-glp-strategies'
  }
];

// FAQ items for trust building
const FAQ_ITEMS = [
  {
    question: 'Is PlayFirst designed to support privacy and data-protection alignment?',
    answer: `Yes. PlayFirst is built with strong technical safeguards and operates in line with POPIA (South Africa), GDPR (EU/UK), and the Australian Privacy Act 1988 (APPs).

**How we protect your data:**
• All data is encrypted in transit (TLS 1.3) and at rest (AES-256)
• We never sell or share your data with third parties
• Strict role-based access controls — only you access your client data
• Audit logging for sensitive actions
• Data hosted on reputable cloud infrastructure with industry-standard physical and network security

**Your privacy rights and controls:**
• Export all of your data at any time (JSON download)
• Permanently delete your account and all associated data
• You decide what is shared with parents — nothing is published without your action
• Lawful basis, consent logging, and a documented Privacy Policy are built in

**AI & Data Processing:**
• AI features process only the minimum information needed for each request (e.g. SOAP content for a parent summary)
• AI providers do not train their models on your inputs
• AI requests are made over encrypted connections to reputable providers (e.g. OpenAI)

**A note for therapists serving US clients (HIPAA):**
PlayFirst is HIPAA-aligned at the technical level (encryption, access controls, audit logs, export/delete). However, full HIPAA compliance for US Protected Health Information (PHI) also requires a signed Business Associate Agreement (BAA) with every vendor in the data chain. We are actively working with our infrastructure and AI providers to enable a BAA-covered HIPAA configuration. **If you serve US-based clients with PHI, please contact us before onboarding them** so we can guide you through a HIPAA-compliant setup (e.g. BYO HIPAA-eligible AI key with BAA, such as Azure OpenAI or AWS Bedrock).

**Best practices we recommend:**
• Use a strong, unique password for your account
• Review shared content before publishing to a parent portal
• Maintain your practice's own data breach response plan and required regulatory documents (e.g. PAIA Manual under POPIA)`,
    icon: Shield
  },
  {
    question: 'How does parent consent work?',
    answer: `Parent access is fully controlled by you, the therapist.

**The consent process:**
1. You decide whether to invite a parent to view their child's portal
2. You generate a unique invite code from the child's profile
3. You share that code with the parent (we recommend documenting this consent in your records)
4. The parent creates their own account using the code

**What parents can see:**
• Weekly Focus activities and goals you've published
• Resources you've explicitly shared
• Session notes you've marked "Share with parent"

**What parents cannot see:**
• Clinical reports (unless you share them)
• Detailed assessment scores
• Notes not marked for sharing
• Other children's information

**Best practice:** Include PlayFirst parent portal access in your standard informed consent documentation.`,
    icon: UserCheck
  },
  {
    question: 'What data do you collect and store?',
    answer: `We collect only what's necessary to provide the service.

**Data we store:**
• Account information (email, name, practice details)
• Child profiles (name, age, interests, goals - as entered by you)
• Session notes and documentation you create
• AI-generated content you choose to save

**Data we don't store:**
• Payment card details (handled securely by Stripe/Paystack)
• Recordings or videos (audio features process locally)
• Biometric data

**Your control:**
• Export your data anytime from Settings
• Delete individual children or your entire account
• All data is deleted within 30 days of account closure

**AI & Privacy:**
• AI features use OpenAI's API with data processing agreements
• We don't use your data to train AI models
• AI requests don't include identifying information beyond what's needed for the specific request`,
    icon: Lock
  },
  {
    question: 'Can I use PlayFirst with clients under 18?',
    answer: `Yes, PlayFirst is designed specifically for pediatric speech therapy practice.

**Age considerations:**
• Most users work with children ages 2-12
• Parental consent is assumed as part of your standard therapy consent process
• We don't collect data directly from children - all data is entered by therapists or parents

**COPPA compliance:**
• We don't knowingly collect personal information from children under 13
• Child profiles are created and managed by adult therapists
• Parent accounts are created by adults using invite codes`,
    icon: Heart
  },
  {
    question: 'How do I handle data for clients who leave my practice?',
    answer: `You have full control over client data retention.

**Options when a client leaves:**
• **Archive**: Keep the profile for your records but mark inactive
• **Export**: Download all notes and reports before deletion
• **Delete**: Permanently remove the child's profile and all associated data

**Recommendations:**
• Follow your state/country's record retention requirements
• Export important documentation to your primary EMR before deletion
• Communicate with parents about data handling in your discharge process

**Note:** Deleted data is permanently removed from our servers within 30 days and cannot be recovered.`,
    icon: FileText
  }
];

// Pricing tiers — Feb 2026 structure
// "Basic" is grandfathered for existing paid users only (kept in backend PLAN_LIMITS,
// removed from public pricing display).
const PRICING_TIERS = [
  // ── PlayFirst Full ─────────────────────────────────────────────
  {
    name: 'Full Free',
    category: 'full',
    monthlyPrice: 0,
    annualPrice: 0,
    description: 'Get started with neuro-affirming documentation',
    features: [
      'Up to 3 children',
      '3 AI reports/month',
      '5 AI activities/month',
      'Unlimited SOAP notes',
      'Parent portal access',
      'Scheduling & Daysheet',
    ],
    cta: 'Start Free',
    popular: false,
    highlight: true,
    plan_id: 'free',
  },
  {
    name: 'Full Pro',
    category: 'full',
    monthlyPrice: 14.99,
    annualPrice: 134.99,
    description: 'For busy solo therapists',
    features: [
      'Up to 30 children',
      '50 AI reports/month',
      '100 AI activities/month',
      'All Free features',
      'Custom letterhead',
      'WhatsApp Assistant',
      'Priority support',
    ],
    cta: 'Get Pro',
    popular: true,
    plan_id: 'pro',
  },
  {
    name: 'Clinical',
    category: 'full',
    monthlyPrice: 39.99,
    annualPrice: 359.99,
    description: 'For clinics & teams',
    features: [
      'Unlimited children',
      'Unlimited AI reports',
      'Unlimited AI activities',
      'Up to 5 therapists included',
      '+$9.99/extra therapist (or $89.99/yr)',
      'WhatsApp Assistant for every therapist',
    ],
    cta: 'Get Clinical',
    popular: false,
    plan_id: 'clinical',
  },
  // ── PlayFirst Lite — stateless workspace (US-friendly, no account) ────
  {
    name: 'Lite Free',
    category: 'lite',
    monthlyPrice: 0,
    annualPrice: 0,
    description: 'Stateless AI tools — no account, no PHI stored',
    features: [
      '5 AI generations/day',
      'Report Builder, Parent Summary, GLP Guide, Activity Generator',
      'Voice dictation + de-ID scanner',
      'PDF/Word/Email export',
      'No data stored on our servers',
    ],
    cta: 'Open Lite',
    popular: false,
    plan_id: 'lite_free',
    href: '/us',
  },
  {
    name: 'Lite Pro',
    category: 'lite',
    monthlyPrice: 9.99,
    annualPrice: 89.99,
    description: 'For solo SLPs who want unlimited Lite tools',
    features: [
      'Unlimited AI generations',
      'All Lite Free tools',
      'Custom writing-style learning',
      'Save handout records to email',
      'Email + magic-link sign-in (no full account needed)',
    ],
    cta: 'Unlock unlimited',
    popular: false,
    plan_id: 'lite_pro',
  },
  // ── PlayFirst Parent — for parents directly (WhatsApp companion + Language Map) ──
  {
    name: 'Parent Monthly',
    category: 'parent',
    monthlyPrice: 6.99,
    annualPrice: 6.99, // shown the same on annual toggle — Parent plans don't follow the SLP billing toggle
    description: 'Easy in, easy out. Pause anytime.',
    features: [
      'Daily play idea — gentle, personalised',
      'Language Map for every child',
      "'Where they're at right now' age-band view",
      'WhatsApp companion + weekly summary',
      'Curated collaborators directory',
    ],
    cta: 'Start monthly',
    popular: false,
    plan_id: 'parent_monthly',
    href: 'https://paystack.shop/pay/3y36h9xeh7',
    external: true,
    parent_period: 'per month',
  },
  {
    name: 'Parent Annual',
    category: 'parent',
    monthlyPrice: 59.99,
    annualPrice: 59.99,
    description: 'Save $24 vs paying monthly.',
    features: [
      'Everything in Monthly',
      'Save ~28% vs monthly',
      'No renewal reminders mid-year',
      'Best value for ongoing families',
    ],
    cta: 'Get annual',
    popular: true,
    plan_id: 'parent_annual',
    href: 'https://paystack.shop/pay/iq5zgoi-ii',
    external: true,
    parent_period: 'per year',
  },
  {
    name: 'Parent Lifetime',
    category: 'parent',
    monthlyPrice: 119.99,
    annualPrice: 119.99,
    description: 'Pay once. Yours for the long play.',
    features: [
      'Every PlayFirst Parent feature',
      'No renewals, no reminders',
      'Lifetime access for your family',
      'One-time payment',
    ],
    cta: 'Get lifetime',
    popular: false,
    plan_id: 'parent_lifetime',
    href: 'https://paystack.shop/pay/-0ecc21xlr',
    external: true,
    parent_period: 'once',
    highlight: true,
  },
];

export default function LandingPage({ onLogin }) {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showDemoEmailCapture, setShowDemoEmailCapture] = useState(false);
  const [showAIPreview, setShowAIPreview] = useState(false);
  const [demoEmail, setDemoEmail] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [activeFeature, setActiveFeature] = useState(0);
  const [showLiteProModal, setShowLiteProModal] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState(() => {
    try { return localStorage.getItem('playfirst_billing_period') || 'monthly'; }
    catch { return 'monthly'; }
  });

  // Persist billing period choice
  useEffect(() => {
    try { localStorage.setItem('playfirst_billing_period', billingPeriod); } catch {/* ignore */}
  }, [billingPeriod]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'therapist',
    data_processing_consent: false,
    invite_code: '',
  });
  const [loading, setLoading] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetCode, setShowResetCode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Anonymous landing-page visit tracking (privacy-aligned)
  useLitePageView();

  // Sign Up / Get Started intent — opens auth dialog directly (US visitors get
  // full access, same as everyone else; the dedicated Lite hub at /us is still
  // available for those who specifically want a stateless workflow).
  const handleSignupIntent = () => {
    setIsLogin(false);
    setShowAuth(true);
  };

  // Parent-with-invite-code intent — pre-selects role=parent and opens the
  // signup form so the invite-code field is visible immediately. Keeps the
  // "I got a code from my therapist" path completely separate from the
  // PlayFirst Parent (self-signup) flow.
  const handleParentInviteIntent = () => {
    setFormData((d) => ({ ...d, role: 'parent' }));
    setIsLogin(false);
    setShowAuth(true);
  };

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % FEATURE_SCREENSHOTS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // POPIA consent guard — only required for therapists (they handle client data)
    if (!isLogin && formData.role === 'therapist' && !formData.data_processing_consent) {
      toast.error('Please confirm the client-data-processing consent to register.');
      return;
    }
    setLoading(true);

    const postAuth = async (timeoutMs = 15000) => {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      // Only send the fields the backend expects; never leak invite_code to /register.
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            email: formData.email,
            password: formData.password,
            name: formData.name,
            role: formData.role,
            data_processing_consent: formData.role === 'therapist'
              ? formData.data_processing_consent
              : true,
          };
      return axios.post(`${API}${endpoint}`, payload, { timeout: timeoutMs });
    };

    try {
      // Auto-retry up to TWICE on transient errors (network blip, cold
      // start, 502/503/504, request timeout) — paying subscribers should
      // never have to click sign-in three times because the platform is
      // briefly saturated. Progressive backoff: 0s, 800ms, 2.5s. Each
      // retry gets a longer timeout to accommodate slow cold-starts.
      let response;
      const attempts = [
        { delay: 0,    timeout: 15000 },
        { delay: 800,  timeout: 20000 },
        { delay: 2500, timeout: 25000 },
      ];
      let lastErr = null;
      for (const { delay, timeout } of attempts) {
        if (delay) await new Promise((r) => setTimeout(r, delay));
        try {
          response = await postAuth(timeout);
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          const status = err.response?.status;
          const transient = !status || status >= 500 || err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK';
          if (!transient) throw err; // 401, 422, 429 — don't retry, surface immediately
        }
      }
      if (lastErr) throw lastErr;

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Parent signup: if they provided an invite code, auto-join the child
      if (!isLogin && formData.role === 'parent' && formData.invite_code.trim()) {
        try {
          await axios.post(
            `${API}/children/join-with-code`,
            null,
            {
              params: { invite_code: formData.invite_code.trim().toUpperCase() },
              headers: { Authorization: `Bearer ${response.data.token}` },
              timeout: 15000,
            },
          );
          toast.success("Welcome! You're now linked to your child's profile. 💛");
        } catch (joinErr) {
          // Account was created — just the join failed. Don't block.
          const detail = joinErr?.response?.data?.detail;
          toast.warning(
            detail
              ? `Account created — but the invite code didn't work: ${detail}. You can enter it later from your dashboard.`
              : "Account created — but we couldn't link the invite code. You can enter it later from your dashboard."
          );
        }
      } else {
        toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      }

      onLogin(response.data.user);
      // Feb 2026 — Dual-role parents (Clinical + paid PFP) should land
      // on PFP "At Home" by default. Global DualRoleTabBar lets them
      // switch to "With my therapist" (Clinical) in one tap without
      // re-authentication. Previously they landed on `/` (Clinical),
      // confusing paying parents into thinking PFP wasn't unlocked.
      const _u = response.data.user || {};
      const _caps = _u.capabilities || {};
      if (_caps.pfp && _caps.clinical_parent) {
        navigate('/parent-app/home');
      } else {
        navigate('/');
      }
    } catch (error) {
      // Distinguish: real network drop vs server slow/overloaded vs auth
      const status = error.response?.status;
      if (!error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        toast.error('Our servers are catching up — please try again in a moment. (If this keeps happening, contact support@playfirstapp.com.)');
      } else if (status === 401) {
        toast.error('Invalid email or password');
      } else if (status === 429) {
        toast.error('Too many attempts — please wait a moment and try again');
      } else if (status >= 500) {
        toast.error('PlayFirst is briefly under heavy load. Please retry in 10–20 seconds.');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed — please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartDemo = async () => {
    // Show AI preview first, then ask for email
    setShowAIPreview(true);
  };

  const handleWatchFullDemo = () => {
    setShowAIPreview(false);
    setShowDemoEmailCapture(true);
  };

  const handleDemoEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!demoEmail || !demoEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      // Track demo start event with email
      await axios.post(`${API}/demo/track-event`, {
        event: 'demo_started',
        metadata: { 
          source: 'landing_page',
          email: demoEmail
        }
      });
    } catch (e) {
      // Continue even if tracking fails
    }
    
    setShowDemoEmailCapture(false);
    setShowDemo(true);
  };

  const handleDemoComplete = () => {
    setShowDemo(false);
    handleSignupIntent();
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Reset code sent! Check your email.');
      setShowForgotPassword(false);
      setShowResetCode(true);
    } catch (error) {
      // Still show the reset code screen (for security, don't reveal if email exists)
      toast.success('If an account exists with this email, you will receive a reset code.');
      setShowForgotPassword(false);
      setShowResetCode(true);
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        email: forgotEmail,
        code: resetCode,
        new_password: newPassword
      });
      toast.success('Password reset successful! Please sign in.');
      setShowResetCode(false);
      setForgotEmail('');
      setResetCode('');
      setNewPassword('');
      setIsLogin(true);
      setShowAuth(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired reset code');
    } finally {
      setResetLoading(false);
    }
  };

  if (showDemo) {
    return <DemoFlow onComplete={handleDemoComplete} onExit={() => setShowDemo(false)} />;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* US Visitor Banner — points to PlayFirst Lite */}
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-[#FDFBF7]/90 backdrop-blur-sm z-50 border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => scrollToSection('resources')} 
              className="flex items-center gap-2 text-sm font-medium text-[#7c6f9c] hover:text-[#5d5478] transition-colors"
            >
              <BookOpen className="w-5 h-5" />
              <span>Free Resources</span>
            </button>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => scrollToSection('features')} className="text-sm text-stone-600 hover:text-primary transition-colors">Features</button>
            <button onClick={() => scrollToSection('faq')} className="text-sm text-stone-600 hover:text-primary transition-colors">FAQ</button>
            <button onClick={() => scrollToSection('pricing')} className="text-sm text-stone-600 hover:text-primary transition-colors">Pricing</button>
            <button onClick={() => scrollToSection('install')} className="text-sm text-stone-600 hover:text-primary transition-colors">Install App</button>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={() => { setIsLogin(true); setShowAuth(true); }}
              className="rounded-full"
              data-testid="nav-signin-btn"
            >
              Sign In
            </Button>
            <Button 
              variant="outline"
              onClick={handleSignupIntent}
              className="rounded-full"
              data-testid="nav-signup-btn"
            >
              Sign Up
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Logo moved here from nav */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <span className="text-2xl font-serif text-primary-900">PlayFirst</span>
            </div>

            <Badge className="mb-6 bg-stone-100 text-stone-600 border-stone-200 px-4 py-1.5">
              <Heart className="w-3 h-3 mr-1" /> Neurodiversity-affirming • Play-based • GLP-informed
            </Badge>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-normal tracking-tight text-primary-900 leading-tight mb-6">
              The clinical companion for <span className="text-[#7c6f9c]">neurodiversity-affirming</span> therapists
            </h1>

            <p className="text-lg md:text-xl leading-relaxed text-stone-600 mb-8 max-w-2xl mx-auto">
              Therapists and parents — pick the path that fits you.
            </p>
          </motion.div>

          {/* Product picker — grouped by audience for clarity */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="max-w-6xl mx-auto mt-4 space-y-10"
            data-testid="landing-product-picker"
          >
            {/* FOR THERAPISTS */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold text-center mb-4" data-testid="audience-therapists-label">
                For Therapists
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* LITE */}
                <button
                  type="button"
                  onClick={() => { window.location.href = '/us'; }}
                  data-testid="picker-lite-btn"
                  className="group text-left rounded-2xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white p-6 hover:border-violet-400 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">
                      PlayFirst Lite
                    </div>
                  </div>
                  <h3 className="text-xl font-serif text-primary-900 mb-1">
                    Fast AI tools — no login, no storage
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed mb-4">
                    Reports, parent summaries, GLP guides, activities, and the Session-to-Home Bridge.
                    Stateless by design — paste de-identified notes, get a draft back.
                    <span className="font-medium text-violet-800"> Works in any region.</span>
                  </p>
                  <div className="flex items-center gap-3 text-xs text-stone-500">
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> No account</span>
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> 5 free/day</span>
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> HIPAA-safe</span>
                  </div>
                  <div className="mt-4 inline-flex items-center text-sm font-semibold text-violet-700 group-hover:gap-2 transition-all">
                    Open Lite
                    <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </button>

                {/* CLINICAL */}
                <button
                  type="button"
                  onClick={handleStartDemo}
                  data-testid="picker-clinical-btn"
                  className="group text-left rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-stone-50 to-white p-6 hover:border-primary hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      <Heart className="w-5 h-5" />
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-primary">
                      PlayFirst · Full practice companion
                    </div>
                  </div>
                  <h3 className="text-xl font-serif text-primary-900 mb-1">
                    Complete EMR-companion for your caseload
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed mb-4">
                    Client profiles, goals, daysheet, parent portal, assessment tracker, session notes,
                    progress reports.
                    <span className="font-medium text-primary-900"> Built for therapists in SA / AU / UK / EU.</span>
                  </p>
                  <div className="flex items-center gap-3 text-xs text-stone-500">
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> POPIA / GDPR-aligned</span>
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> Parent portal</span>
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> Free trial</span>
                  </div>
                  <div className="mt-4 inline-flex items-center text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                    Try the Full demo
                    <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </button>
              </div>
            </div>

            {/* FOR PARENTS */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold text-center mb-4" data-testid="audience-parents-label">
                For Parents
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* INVITE CODE — therapist invited me */}
                <button
                  type="button"
                  onClick={handleParentInviteIntent}
                  data-testid="picker-parent-invite-btn"
                  className="group text-left rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 hover:border-amber-400 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                      <UserCheck className="w-5 h-5" />
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                      My therapist invited me
                    </div>
                  </div>
                  <h3 className="text-xl font-serif text-primary-900 mb-1">
                    I have an invite code
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed mb-4">
                    Got a code from your speech therapist? Enter it here to see your child's
                    shared goals, weekly focus, and play ideas — straight from their clinician.
                  </p>
                  <div className="flex items-center gap-3 text-xs text-stone-500">
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> Free for parents</span>
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> Linked to your therapist</span>
                  </div>
                  <div className="mt-4 inline-flex items-center text-sm font-semibold text-amber-700 group-hover:gap-2 transition-all">
                    Enter my invite code
                    <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </button>

                {/* PARENT (self-managed) — the warm hug card */}
                <button
                  type="button"
                  onClick={() => { window.location.href = '/parent-app/pricing'; }}
                  data-testid="picker-parent-btn"
                  className="group text-left rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white p-6 hover:border-rose-400 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center">
                      <Heart className="w-5 h-5" />
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">
                      PlayFirst Parent
                    </div>
                  </div>
                  <h3 className="text-xl font-serif text-primary-900 mb-1">
                    For parents finding their way
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed mb-4">
                    For parents who are worried, overwhelmed, or just want to play more.
                    One gentle, personalised play idea at a time. From newborn onwards.
                    <span className="font-medium text-rose-800"> No diagnosis. No pressure. Just connection.</span>
                  </p>
                  <div className="flex items-center gap-3 text-xs text-stone-500">
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> WhatsApp companion</span>
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> From birth onwards</span>
                  </div>
                  <div className="mt-4 inline-flex items-center text-sm font-semibold text-rose-700 group-hover:gap-2 transition-all">
                    See what's included
                    <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Secondary actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex items-center justify-center gap-2 mt-6"
          >
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setIsLogin(true); setShowAuth(true); }}
              className="rounded-full text-sm"
              data-testid="hero-sign-in-btn"
            >
              Sign in
            </Button>
            <span className="text-stone-300">·</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSignupIntent}
              className="rounded-full text-sm"
              data-testid="hero-sign-up-btn"
            >
              Create account
            </Button>
            <span className="text-stone-300">·</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleStartDemo}
              className="rounded-full text-sm"
              data-testid="hero-get-started-btn"
            >
              Quick demo
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Original-style feature strap below (keeps SEO content + scroll depth) */}
      <section className="pb-10 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm text-stone-500">
            Strengths-based language • GLP &amp; NLA frameworks • Child-led, play-based documentation
          </p>
        </div>
      </section>

      {/* Values Section - Neurodiversity-Affirming */}
      <section className="py-12 px-4 bg-gradient-to-r from-stone-50 via-[#f5f3f8] to-amber-50/50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-serif text-primary-900 mb-3">
              Built on neurodiversity-affirming values
            </h2>
            <p className="text-stone-600">
              PlayFirst is designed by SLPs who celebrate neurological differences and honor every child's communication journey
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <Heart className="w-6 h-6" />,
                title: 'Neurodiversity-Affirming',
                description: 'We celebrate neurological differences. Our AI uses strengths-based, affirming language that respects each child\'s unique way of communicating — never deficit-focused.',
                color: 'from-[#7c6f9c] to-[#6b5b8a]'
              },
              {
                icon: <Play className="w-6 h-6" />,
                title: 'Play-Based & Child-Led',
                description: 'Play is the foundation of language learning. Our tools support child-led intervention with activities centered on each child\'s interests and natural contexts.',
                color: 'from-[#9c8b7c] to-[#8a7a6b]'
              },
              {
                icon: <Sparkles className="w-6 h-6" />,
                title: 'GLP & NLA Informed',
                description: 'Full support for Gestalt Language Processing. Track NLA stages 1-6, document gestalt development, and generate reports that truly understand the GLP framework.',
                color: 'from-[#7c9c8b] to-[#6b8a7a]'
              }
            ].map((value, idx) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="p-6 h-full rounded-2xl border-0 shadow-md hover:shadow-lg transition-shadow bg-white">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${value.color} text-white flex items-center justify-center mb-4`}>
                    {value.icon}
                  </div>
                  <h3 className="font-serif text-lg text-primary-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{value.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Showcase Section */}
      <section id="features" className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              Everything you need to run your practice
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              From assessment to report to scheduling — all seamlessly connected
            </p>
          </motion.div>

          {/* Feature Cards Carousel */}
          <div className="relative">
            {/* Feature Navigation */}
            <div className="flex justify-center gap-2 mb-8">
              {FEATURE_SCREENSHOTS.map((feature, idx) => (
                <button
                  key={feature.id}
                  onClick={() => setActiveFeature(idx)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                    activeFeature === idx 
                      ? 'bg-primary text-white shadow-md' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {feature.icon}
                  <span className="hidden sm:inline">{feature.title.split(' ')[0]}</span>
                </button>
              ))}
            </div>

            {/* Feature Display */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="overflow-hidden rounded-2xl border-stone-200 shadow-xl">
                  <div className="grid md:grid-cols-2">
                    {/* Feature Mockup Visual */}
                    <div className="bg-gradient-to-br from-stone-50 to-stone-100 p-6 md:p-8 min-h-[300px] md:min-h-[400px]">
                      <div className="bg-white rounded-xl shadow-lg border border-stone-200 h-full p-4 md:p-6">
                        {/* Mockup Header */}
                        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-stone-100">
                          <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-400" />
                            <div className="w-3 h-3 rounded-full bg-yellow-400" />
                            <div className="w-3 h-3 rounded-full bg-green-400" />
                          </div>
                          <div className="flex-1 text-center">
                            <span className="text-xs text-stone-500">{FEATURE_SCREENSHOTS[activeFeature].mockupTitle}</span>
                          </div>
                        </div>
                        
                        {/* Mockup Content */}
                        <div className="space-y-3">
                          {FEATURE_SCREENSHOTS[activeFeature].mockupItems.map((item, idx) => (
                            <motion.div
                              key={item}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 hover:bg-primary/5 transition-colors"
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                idx === 0 ? 'bg-primary/20 text-primary' :
                                idx === 1 ? 'bg-green-100 text-green-600' :
                                idx === 2 ? 'bg-blue-100 text-blue-600' :
                                'bg-amber-100 text-amber-600'
                              }`}>
                                <CheckCircle className="w-4 h-4" />
                              </div>
                              <span className="text-sm text-stone-700 font-medium">{item}</span>
                            </motion.div>
                          ))}
                        </div>
                        
                        {/* Mockup Footer */}
                        <div className="mt-4 pt-3 border-t border-stone-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                                {FEATURE_SCREENSHOTS[activeFeature].icon}
                              </div>
                              <span className="text-xs text-stone-500">PlayFirst</span>
                            </div>
                            <div className="h-2 w-20 bg-primary/20 rounded-full overflow-hidden">
                              <div className="h-full w-3/4 bg-primary rounded-full" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Feature Info */}
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                          {FEATURE_SCREENSHOTS[activeFeature].icon}
                        </div>
                        <Badge variant="secondary">Feature {activeFeature + 1} of {FEATURE_SCREENSHOTS.length}</Badge>
                      </div>
                      
                      <h3 className="text-2xl font-serif text-primary-900 mb-3">
                        {FEATURE_SCREENSHOTS[activeFeature].title}
                      </h3>
                      
                      <p className="text-stone-600 mb-6 leading-relaxed">
                        {FEATURE_SCREENSHOTS[activeFeature].description}
                      </p>
                      
                      <Button 
                        onClick={handleStartDemo}
                        className="rounded-full self-start"
                      >
                        Try it in Demo <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button
              onClick={() => setActiveFeature((prev) => (prev - 1 + FEATURE_SCREENSHOTS.length) % FEATURE_SCREENSHOTS.length)}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-stone-600 hover:text-primary transition-colors hidden md:flex"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setActiveFeature((prev) => (prev + 1) % FEATURE_SCREENSHOTS.length)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-stone-600 hover:text-primary transition-colors hidden md:flex"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Quick Feature Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-12">
            {FEATURE_SCREENSHOTS.map((feature, idx) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => setActiveFeature(idx)}
                className={`p-4 rounded-xl cursor-pointer transition-all ${
                  activeFeature === idx 
                    ? 'bg-primary/10 border-2 border-primary' 
                    : 'bg-stone-50 border-2 border-transparent hover:border-stone-200'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                  activeFeature === idx ? 'bg-primary text-white' : 'bg-stone-200 text-stone-600'
                }`}>
                  {feature.icon}
                </div>
                <h4 className="font-medium text-primary-900 text-sm">{feature.title}</h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Connected Flow */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-stone-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              See how it all connects
            </h2>
            <p className="text-lg text-stone-600">
              One seamless workflow from session to documentation
            </p>
          </motion.div>

          {/* Connected Flow Visualization */}
          <div className="relative">
            {/* Connection Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary/20 via-primary to-primary/20 hidden md:block" />
            
            {[
              { step: 1, title: 'Generate Report', desc: 'AI creates professional clinical reports from your session notes', icon: <FileText /> },
              { step: 2, title: 'Save SOAP Note', desc: 'Document sessions with auto-populated SOAP format', icon: <ClipboardCheck /> },
              { step: 3, title: 'Auto-Update Daysheet', desc: 'Your schedule updates automatically when notes are saved', icon: <Calendar /> },
              { step: 4, title: 'Plan Activities', desc: 'AI suggests activities based on goals and progress', icon: <Sparkles /> }
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className={`relative flex items-center gap-6 mb-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <Card className="p-6 rounded-2xl inline-block text-left">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                        {item.icon}
                      </div>
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                        Step {item.step}
                      </span>
                    </div>
                    <h3 className="font-serif text-lg text-primary-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-stone-600">{item.desc}</p>
                  </Card>
                </div>
                
                {/* Center Node */}
                <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-medium shadow-lg shadow-primary/30 z-10 flex-shrink-0">
                  {item.step}
                </div>
                
                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button 
              size="lg"
              onClick={handleStartDemo}
              className="rounded-full"
            >
              Try the Demo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-stone-600 mb-2">
              Plans for therapists, clinics, and parents
            </p>
            <p className="text-xs text-stone-500 mb-6">
              Monthly / Annual toggle below applies to therapist plans. Parent plans are paid directly via Paystack.
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-stone-100 border border-stone-200" data-testid="billing-toggle">
              <button
                type="button"
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${billingPeriod === 'monthly' ? 'bg-white text-primary-900 shadow-sm' : 'text-stone-500'}`}
                data-testid="billing-monthly-btn"
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingPeriod('annual')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${billingPeriod === 'annual' ? 'bg-white text-primary-900 shadow-sm' : 'text-stone-500'}`}
                data-testid="billing-annual-btn"
              >
                Annual
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">Save 25%</span>
              </button>
            </div>
          </motion.div>

          {/* PlayFirst Full — 3 tiers */}
          <div className="mb-3">
            <h3 className="text-sm uppercase tracking-widest text-stone-500 font-semibold">PlayFirst Full</h3>
            <p className="text-sm text-stone-500">Full clinical workspace · child profiles · session tracking · parent portal</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            {PRICING_TIERS.filter((t) => t.category === 'full').map((tier, idx) => (
              <PricingCard key={tier.name} tier={tier} idx={idx} billingPeriod={billingPeriod} onCta={(t) => t.price === 0 ? handleStartDemo() : handleSignupIntent()} />
            ))}
          </div>

          {/* PlayFirst Lite — 2 tiers */}
          <div className="mb-3">
            <h3 className="text-sm uppercase tracking-widest text-stone-500 font-semibold">PlayFirst Lite</h3>
            <p className="text-sm text-stone-500">Stateless AI workspace · no account · no PHI stored · perfect for US-based SLPs (HIPAA Safe Harbor)</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mb-12">
            {PRICING_TIERS.filter((t) => t.category === 'lite').map((tier, idx) => (
              <PricingCard
                key={tier.name}
                tier={tier}
                idx={idx}
                billingPeriod={billingPeriod}
                onCta={(t) => {
                  if (t.plan_id === 'lite_pro') { setShowLiteProModal(true); }
                  else if (t.href) { navigate(t.href); }
                  else { navigate('/us'); }
                }}
              />
            ))}
          </div>

          {/* PlayFirst Parent — 3 tiers (Monthly / Annual / Lifetime) */}
          <div className="mb-3">
            <h3 className="text-sm uppercase tracking-widest text-stone-500 font-semibold" data-testid="pricing-parent-heading">
              PlayFirst Parent
            </h3>
            <p className="text-sm text-stone-500">
              For parents directly · daily play idea · Language Map · WhatsApp companion · from birth onwards
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRICING_TIERS.filter((t) => t.category === 'parent').map((tier, idx) => (
              <PricingCard
                key={tier.name}
                tier={tier}
                idx={idx}
                billingPeriod={billingPeriod}
                onCta={(t) => {
                  if (t.href) {
                    window.open(t.href, '_blank', 'noopener,noreferrer');
                  } else {
                    navigate('/parent-app/pricing');
                  }
                }}
              />
            ))}
          </div>

          <p className="text-center text-xs text-stone-500 mt-3">
            <button
              type="button"
              onClick={() => navigate('/parent-app/pricing')}
              className="underline hover:text-[#7C6F9C]"
              data-testid="pricing-parent-see-full"
            >
              See the full PlayFirst Parent page →
            </button>
          </p>

          <p className="text-center text-sm text-stone-500 mt-10">
            All prices in USD · Billed via Paystack (works globally; ZAR rate for South African users)
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              What our community says
            </h2>
            <p className="text-base md:text-lg text-stone-600">
              Real feedback from therapists and parents using PlayFirst
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-6 rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50/50 to-white h-full">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full">Therapist</span>
                </div>
                <blockquote className="text-stone-700 text-sm leading-relaxed italic">
                  "PlayFirst has been a wonderful investment for my practice. I am really enjoying using it! It has saved me a significant amount of admin time and has streamlined both my therapy planning and the way I share information with parents.
                  <br /><br />
                  The resources are practical, well-structured, and easy to adapt to each child's individual needs.
                  <br /><br />
                  Overall, it has enhanced both my efficiency and the quality of support I'm able to provide."
                </blockquote>
                <p className="mt-4 text-sm font-medium text-purple-700">— MA, PlayFirst therapist</p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 rounded-2xl border border-teal-100 bg-gradient-to-br from-teal-50/50 to-white h-full">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-medium bg-teal-100 text-teal-700 px-2.5 py-1 rounded-full">Parent</span>
                </div>
                <blockquote className="text-stone-700 text-sm leading-relaxed italic">
                  "PlayFirst has made a meaningful difference for us. We've seen clear carryover of therapy goals into daily routines — self-advocacy, making choices, and functional communication.
                  <br /><br />
                  The strategies are practical and easy to use at home, and our child really enjoys the home activities. It's been wonderful to see how naturally these skills are used in everyday life.
                  <br /><br />
                  We are truly grateful."
                </blockquote>
                <p className="mt-4 text-sm font-medium text-teal-700">— BM, PlayFirst parent</p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Trust Building */}
      <section id="faq" className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-stone-100 text-stone-700 border-stone-200">
              <HelpCircle className="w-3 h-3 mr-1" /> Common Questions
            </Badge>
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              Privacy, Security & Compliance
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              We know you have questions about keeping your clients' data safe. Here's what you need to know.
            </p>
          </motion.div>

          <div className="space-y-4">
            {FAQ_ITEMS.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="rounded-2xl border-stone-200 overflow-hidden">
                  <details className="group">
                    <summary className="flex items-center gap-4 p-5 cursor-pointer list-none hover:bg-stone-50 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                        <item.icon className="w-5 h-5 text-stone-600" />
                      </div>
                      <span className="font-medium text-primary-900 flex-1 text-left">
                        {item.question}
                      </span>
                      <ChevronDown className="w-5 h-5 text-stone-400 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="px-5 pb-5 pt-0">
                      <div className="pl-14 text-sm text-stone-600 leading-relaxed whitespace-pre-line">
                        {item.answer}
                      </div>
                    </div>
                  </details>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-stone-500 mb-4">
              Have more questions about privacy or security?
            </p>
            <Button 
              variant="outline"
              onClick={() => window.location.href = 'mailto:michaela@playfirstapp.com'}
              className="rounded-full"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Resource Hub Section - SEO Content */}
      <section id="resources" className="py-16 px-4 bg-gradient-to-b from-stone-50 to-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-[#f5f3f8] text-[#7c6f9c] border-[#e0dce8]">
              <BookOpen className="w-3 h-3 mr-1" /> Free Resources
            </Badge>
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              Learn about neurodiversity-affirming practice
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Free guides and articles to support your GLP and affirming therapy journey
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {RESOURCE_ARTICLES.map((article, idx) => (
              <motion.a
                key={article.id}
                href={article.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="block"
              >
                <Card className="h-full rounded-2xl border-stone-200 hover:border-[#b8b0c9] hover:shadow-lg transition-all cursor-pointer group overflow-hidden">
                  {/* Category Banner */}
                  <div className="bg-gradient-to-r from-[#f5f3f8] to-stone-100 px-4 py-2">
                    <span className="text-xs font-medium text-[#7c6f9c]">{article.category}</span>
                  </div>
                  
                  <div className="p-5">
                    <h3 className="font-serif text-lg text-primary-900 mb-2 group-hover:text-[#7c6f9c] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-stone-600 leading-relaxed mb-4">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-stone-500">{article.readTime}</span>
                      <span className="text-xs text-[#7c6f9c] font-medium flex items-center gap-1 group-hover:underline">
                        Read Article <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Card>
              </motion.a>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-sm text-stone-500 mb-4">
              More resources coming soon! Sign up to get notified when new articles are published.
            </p>
            <Button 
              variant="outline"
              onClick={handleSignupIntent}
              className="rounded-full"
            >
              Sign Up for Updates
            </Button>
          </div>
        </div>
      </section>

      {/* Install App Section */}
      <section id="install" className="py-16 px-4 bg-gradient-to-b from-white to-[#FDFBF7]">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 rounded-3xl bg-gradient-to-br from-primary/5 to-primary/10 border-0">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <Badge className="mb-4 bg-primary/20 text-primary border-0">
                  <Smartphone className="w-3 h-3 mr-1" /> Progressive Web App
                </Badge>
                <h2 className="text-2xl md:text-3xl font-serif text-primary-900 mb-4">
                  Add PlayFirst to your home screen
                </h2>
                <p className="text-stone-600 mb-6">
                  Access PlayFirst like a native app — works offline and launches instantly from your home screen.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-primary flex-shrink-0">
                      <Share className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-primary-900 text-sm">iOS (Safari)</p>
                      <p className="text-xs text-stone-600">Tap Share → "Add to Home Screen"</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-primary flex-shrink-0">
                      <MoreVertical className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-primary-900 text-sm">Android (Chrome)</p>
                      <p className="text-xs text-stone-600">Tap Menu → "Install App" or "Add to Home Screen"</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center">
                <div className="w-48 h-48 rounded-3xl bg-white shadow-xl flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      <Heart className="w-8 h-8 text-primary" />
                    </div>
                    <p className="font-serif text-primary-900">PlayFirst</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 border-t border-stone-100 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-5 h-5 text-primary" />
                <span className="font-serif text-primary-900 text-lg">PlayFirst</span>
              </div>
              <p className="text-sm text-stone-500">
                Built for speech therapists, with love.
              </p>
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-stone-700">Get in touch</p>
              <a href="mailto:michaela@playfirstapp.com" className="flex items-center gap-2 text-sm text-stone-600 hover:text-primary transition-colors">
                <Mail className="w-4 h-4" /> michaela@playfirstapp.com
              </a>
              <a href="https://instagram.com/playfirstapp" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-stone-600 hover:text-primary transition-colors">
                <Instagram className="w-4 h-4" /> @playfirstapp
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a href="/terms" className="text-sm text-stone-600 hover:text-primary">Terms</a>
              <a href="/privacy" className="text-sm text-stone-600 hover:text-primary">Privacy Policy</a>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-stone-200 text-center">
            <p className="text-xs text-stone-400">
              © {new Date().getFullYear()} PlayFirst. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-center font-serif text-2xl">
              {isLogin ? 'Welcome back' : 'Create your account'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {!isLogin && (
              <div className="space-y-2" data-testid="landing-role-picker">
                <Label className="text-sm">I'm signing up as a…</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'therapist' })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      formData.role === 'therapist'
                        ? 'border-stone-900 bg-stone-50 ring-2 ring-stone-200'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid="landing-role-therapist"
                  >
                    <div className="text-2xl mb-1" aria-hidden="true">🎓</div>
                    <div className="font-medium text-sm text-stone-900">Speech therapist</div>
                    <div className="text-xs text-stone-500 mt-0.5">I treat clients</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'parent' })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      formData.role === 'parent'
                        ? 'border-stone-900 bg-stone-50 ring-2 ring-stone-200'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid="landing-role-parent"
                  >
                    <div className="text-2xl mb-1" aria-hidden="true">💛</div>
                    <div className="font-medium text-sm text-stone-900">Parent with a therapist</div>
                    <div className="text-xs text-stone-500 mt-0.5">I have an invite code from my SLP</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAuth(false);
                      navigate('/parent-app/pricing');
                    }}
                    className="p-3 rounded-xl border-2 text-left transition-all border-stone-200 hover:border-[#7C6F9C] hover:bg-[#7C6F9C]/5"
                    data-testid="landing-role-parent-self"
                  >
                    <div className="text-2xl mb-1" aria-hidden="true">✨</div>
                    <div className="font-medium text-sm text-stone-900">Parent looking for support</div>
                    <div className="text-xs text-stone-500 mt-0.5">Daily play ideas, no therapist needed →</div>
                  </button>
                </div>
                <p className="text-xs text-stone-500 leading-relaxed pt-1">
                  Have a partner discount code? Choose <em>Parent looking for support</em> above — you'll
                  enter the code on the Pricing page.
                </p>
              </div>
            )}

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">
                  {formData.role === 'parent' ? 'Your name' : 'Full name'}
                </Label>
                <Input
                  id="name"
                  placeholder={formData.role === 'parent' ? 'Sarah Johnson' : 'Dr. Jane Smith'}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  className="rounded-lg"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="landing-email-input"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="rounded-lg"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="landing-password-input"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="rounded-lg"
              />
            </div>
            
            {!isLogin && formData.role === 'parent' && (
              <div className="space-y-2">
                <Label htmlFor="invite_code" className="text-sm">
                  Invite code <span className="text-stone-400">(optional)</span>
                </Label>
                <Input
                  id="invite_code"
                  placeholder="e.g. AB12CD34"
                  value={formData.invite_code}
                  onChange={(e) => setFormData({ ...formData, invite_code: e.target.value })}
                  className="rounded-lg uppercase tracking-wider"
                  maxLength={12}
                  data-testid="landing-invite-code"
                />
                <p className="text-xs text-stone-500 leading-relaxed">
                  Got a code from your therapist? Enter it here to instantly link your child's profile.
                  No code yet? Skip it — your therapist can share one later.
                </p>
              </div>
            )}

            {!isLogin && formData.role === 'therapist' && (
              <label
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  formData.data_processing_consent
                    ? 'border-emerald-300 bg-emerald-50/50'
                    : 'border-amber-300 bg-amber-50/40'
                }`}
                data-testid="landing-consent-label"
              >
                <input
                  type="checkbox"
                  checked={!!formData.data_processing_consent}
                  onChange={(e) => setFormData({ ...formData, data_processing_consent: e.target.checked })}
                  className="mt-1 flex-shrink-0"
                  data-testid="landing-consent-checkbox"
                  required
                />
                <span className="text-xs text-stone-800 leading-relaxed">
                  I confirm that I have obtained appropriate consent from clients, parents or legal guardians to capture and process client information using PlayFirst.
                </span>
              </label>
            )}

            <Button
              type="submit"
              className="w-full rounded-full"
              disabled={loading || (!isLogin && formData.role === 'therapist' && !formData.data_processing_consent)}
              data-testid="landing-submit-btn"
            >
              {loading
                ? 'Please wait...'
                : (isLogin
                    ? 'Sign In'
                    : (formData.role === 'parent' ? 'Create parent account' : 'Create therapist account')
                  )
              }
            </Button>
            {!isLogin && (
              <p className="text-xs text-stone-400 text-center mt-2">
                By creating an account, you agree to our{' '}
                <a href="/terms" className="underline hover:text-stone-600">Terms of Service</a> and{' '}
                <a href="/privacy" className="underline hover:text-stone-600">Privacy Policy</a>.
              </p>
            )}
          </form>
          
          {isLogin && (
            <div className="text-center mt-3">
              <button
                type="button"
                onClick={() => { setShowAuth(false); setShowForgotPassword(true); }}
                className="text-sm text-[#7c6f9c] hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </div>
          )}
          
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-stone-600 hover:text-primary"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-center font-serif text-2xl">Reset your password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            <p className="text-sm text-stone-600 text-center">
              Enter your email address and we'll send you a code to reset your password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="rounded-lg"
                data-testid="forgot-email-input"
              />
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={resetLoading}>
              {resetLoading ? 'Sending...' : 'Send Reset Code'}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setShowForgotPassword(false); setShowAuth(true); setIsLogin(true); }}
                className="text-sm text-stone-600 hover:text-primary"
              >
                Back to sign in
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Code Dialog */}
      <Dialog open={showResetCode} onOpenChange={setShowResetCode}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="reset-code-dialog">
          <DialogHeader>
            <DialogTitle className="text-center font-serif text-2xl">Enter reset code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <p className="text-sm text-stone-600 text-center">
              We've sent an 8-character code to <strong>{forgotEmail}</strong>. Enter it below with your new password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="reset-code">Reset Code</Label>
              <Input
                id="reset-code"
                type="text"
                placeholder="ABCD1234"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                required
                maxLength={8}
                className="rounded-lg text-center text-2xl tracking-widest font-mono"
                data-testid="reset-code-input"
              />
              <p className="text-xs text-stone-500 text-center">Enter the 8-character code from your email</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="rounded-lg"
                data-testid="new-password-input"
              />
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={resetLoading}>
              {resetLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setShowResetCode(false); setShowForgotPassword(true); }}
                className="text-sm text-stone-600 hover:text-primary"
              >
                Didn't receive a code? Try again
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Demo Email Capture Dialog */}
      <Dialog open={showDemoEmailCapture} onOpenChange={setShowDemoEmailCapture}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-center font-serif text-2xl">
              Try the Interactive Demo
            </DialogTitle>
          </DialogHeader>
          
          <div className="text-center text-stone-600 text-sm mt-2">
            Enter your email to start exploring PlayFirst's features. 
            We'll send you helpful tips and updates.
          </div>
          
          <form onSubmit={handleDemoEmailSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="demo-email">Email Address</Label>
              <Input
                id="demo-email"
                type="email"
                placeholder="you@example.com"
                value={demoEmail}
                onChange={(e) => setDemoEmail(e.target.value)}
                required
                className="rounded-lg"
                data-testid="demo-email-input"
              />
            </div>
            
            <Button type="submit" className="w-full rounded-full" data-testid="start-demo-btn">
              <Play className="w-4 h-4 mr-2" />
              Start Full Demo
            </Button>
          </form>
          
          <p className="text-xs text-center text-stone-400 mt-2">
            No credit card required. We respect your privacy.
          </p>
        </DialogContent>
      </Dialog>

      {/* AI Preview Dialog - Shows magic first, then asks for email */}
      <Dialog open={showAIPreview} onOpenChange={setShowAIPreview}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center font-serif text-2xl">
              See PlayFirst in Action
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Example AI Report Preview */}
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-6 border border-teal-100">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-teal-600" />
                <span className="font-medium text-teal-800">AI-Generated Progress Report</span>
                <Badge className="ml-auto bg-teal-100 text-teal-700 text-xs">Generated in 8 seconds</Badge>
              </div>
              
              <div className="bg-white rounded-xl p-4 text-sm text-stone-700 space-y-3 shadow-sm">
                <p className="font-medium text-stone-900">Progress Summary - Emma, Age 4</p>
                <p>Emma continues to demonstrate strong progress as a <span className="text-teal-600 font-medium">gestalt language processor at NLA Stage 2</span>. She is actively mitigating her gestalts and beginning to combine single words with gestalt chunks.</p>
                <p>Notable developments include spontaneous use of <span className="italic">"want + [item]"</span> combinations during play-based activities, particularly when engaging with her special interests (water play, musical instruments).</p>
                <p className="text-stone-500 text-xs">... continues with goals, strategies, and recommendations</p>
              </div>
            </div>
            
            {/* Key Features */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-stone-50 rounded-xl">
                <FileText className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm font-medium">SOAP Notes</p>
                <p className="text-xs text-stone-500">GLP-informed templates</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-xl">
                <Calendar className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm font-medium">Scheduling</p>
                <p className="text-xs text-stone-500">Daysheet & timetable</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-xl">
                <Heart className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm font-medium">Parent Portal</p>
                <p className="text-xs text-stone-500">Home carryover activities</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-xl">
                <Lightbulb className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm font-medium">AI Activities</p>
                <p className="text-xs text-stone-500">Based on interests</p>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="space-y-3 pt-2">
              <Button 
                onClick={handleWatchFullDemo}
                className="w-full rounded-full"
                size="lg"
              >
                <Play className="w-4 h-4 mr-2" />
                Try Interactive Demo
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => { setShowAIPreview(false); handleSignupIntent(); }}
                className="w-full rounded-full"
              >
                Sign Up Free
              </Button>
            </div>
            
            <p className="text-xs text-center text-stone-400">
              Free plan includes 3 children, 3 AI reports/month
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* US Signup Intent Modal — REMOVED. US visitors now have full access to PlayFirst.
          The /us hub remains for those who specifically want the stateless Lite workflow. */}

      {/* Lite Pro checkout modal — renders when user clicks "Unlock unlimited" on Lite tier */}
      <LiteProCheckoutModal
        open={showLiteProModal}
        onClose={() => setShowLiteProModal(false)}
        billingPeriod={billingPeriod}
      />
    </div>
  );
}

function PricingCard({ tier, idx, billingPeriod, onCta }) {
  const isParent = tier.category === 'parent';
  const isAnnual = billingPeriod === 'annual';
  const price = isParent ? tier.monthlyPrice : (isAnnual ? tier.annualPrice : tier.monthlyPrice);
  const isFree = price === 0;
  const periodLabel = isParent
    ? (tier.parent_period || '')
    : (isFree ? 'forever free' : (isAnnual ? '/yr' : '/mo'));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.08 }}
    >
      <Card className={`p-5 h-full rounded-2xl relative flex flex-col ${
        tier.popular ? 'border-2 border-primary shadow-xl md:scale-105 z-10' :
        tier.highlight ? 'border-2 border-emerald-500 bg-emerald-50/30' : 'border-stone-200'
      }`} data-testid={`pricing-card-${tier.plan_id}`}>
        {tier.popular && (
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-xs">
            Most Popular
          </Badge>
        )}
        {tier.highlight && (
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-xs">
            Start Here
          </Badge>
        )}

        <div className="text-center mb-4">
          <h3 className="text-lg font-serif text-primary-900 mb-1">{tier.name}</h3>
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-3xl font-bold text-primary-900">
              ${price}
            </span>
            <span className="text-stone-500 text-sm">
              {periodLabel}
            </span>
          </div>
          {isAnnual && tier.monthlyPrice > 0 && !isParent && (
            <p className="text-xs text-emerald-700 font-medium mt-1">
              Save ${(tier.monthlyPrice * 12 - tier.annualPrice).toFixed(2)}/yr · 25% off
            </p>
          )}
          <p className="text-xs text-stone-600 mt-1">{tier.description}</p>
        </div>

        <ul className="space-y-2 mb-3 flex-1">
          {tier.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-stone-700">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
              {feature}
            </li>
          ))}
          {(tier.coming_soon_features || []).map((feature, i) => (
            <li key={`cs-${i}`} className="flex items-start gap-2 text-xs text-amber-800/80" data-testid={`coming-soon-${tier.plan_id}-${i}`}>
              <span className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-600">⏳</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          className={`w-full rounded-full text-sm ${tier.highlight ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
          variant={tier.popular ? 'default' : tier.highlight ? 'default' : 'outline'}
          size="sm"
          onClick={() => onCta?.(tier)}
          data-testid={`pricing-cta-${tier.plan_id}`}
        >
          {tier.cta}
        </Button>
      </Card>
    </motion.div>
  );
}

